import React, { useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import AppTooltip from '../../../../atomic/tooltip/AppTooltip';
import { SectionHeader } from '../components/SectionHeader';
import { CreateCouponModal } from '../components/CreateCouponModal';
import {
  formatCouponCardTitle,
  formatCouponCardSubtitle,
  formatCouponCardFooter,
  couponListCategoryFromPayload,
} from '../couponForm';

const COUPON_CATEGORIES = [
  { id: 'all', label: 'All Codes' },
  { id: 'new-to-store', label: 'New-to-store' },
  { id: 'old-to-store', label: 'Old-to-store' },
  { id: 'dedicated-customer', label: 'Dedicated Customer' },
];

const INITIAL_COUPONS = [
  {
    id: 'c1',
    title: '$0 Delivery',
    subtitle: 'when you spend $10+',
    footer: 'Terms apply Sep 30',
    source: 'platform',
    category: 'all',
  },
  {
    id: 'c2',
    title: '$10 off first order',
    subtitle: 'new customers only',
    footer: 'Terms apply Sep 30',
    source: 'platform',
    category: 'new-to-store',
  },
  {
    id: 'c3',
    title: 'Buy 1 Get 1',
    subtitle: 'on select items',
    footer: 'Set by Restaurant',
    source: 'restaurant',
    category: 'old-to-store',
  },
  {
    id: 'c4',
    title: '20% off',
    subtitle: 'orders over $15',
    footer: 'Set by Restaurant',
    source: 'restaurant',
    category: 'dedicated-customer',
  },
];

function mapPayloadToCard(payload) {
  return {
    id: `local-${payload.code}-${Date.now()}`,
    title: formatCouponCardTitle(payload),
    subtitle: formatCouponCardSubtitle(payload),
    footer: formatCouponCardFooter(payload),
    source: payload.promoCodeOwner === 'kitchen' ? 'restaurant' : 'platform',
    category: couponListCategoryFromPayload(payload),
    payload,
  };
}

function CouponCard({ coupon, selected, onSelect }) {
  const sourceLabel = coupon.source === 'platform' ? 'Platform' : 'Restaurant';

  return (
    <button
      type='button'
      className={selected ? 'ss-coupon-card ss-coupon-card--selected' : 'ss-coupon-card'}
      onClick={() => onSelect(coupon)}
      aria-pressed={selected}
    >
      <span
        className={
          coupon.source === 'platform'
            ? 'ss-coupon-card__source ss-coupon-card__source--platform'
            : 'ss-coupon-card__source ss-coupon-card__source--restaurant'
        }
      >
        {sourceLabel}
      </span>
      <div className='ss-coupon-card__body'>
        <div className='ss-coupon-card__title'>{coupon.title}</div>
        <div className='ss-coupon-card__subtitle'>{coupon.subtitle}</div>
      </div>
      <div className='ss-coupon-card__divider' aria-hidden='true' />
      <div className='ss-coupon-card__footer'>{coupon.footer}</div>
    </button>
  );
}

export function CouponsSection({ meta }) {
  const [activeCategory, setActiveCategory] = useState('all');
  const [coupons, setCoupons] = useState(INITIAL_COUPONS);
  const [displayedCouponId, setDisplayedCouponId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const filteredCoupons = useMemo(() => {
    if (activeCategory === 'all') return coupons;
    return coupons.filter((c) => c.category === activeCategory);
  }, [activeCategory, coupons]);

  const displayedCoupon = coupons.find((c) => c.id === displayedCouponId) ?? null;

  const handleCreate = (payload) => {
    const card = mapPayloadToCard(payload);
    setCoupons((prev) => [card, ...prev]);
    setDisplayedCouponId(card.id);
    if (card.category !== 'all') {
      setActiveCategory(card.category);
    }
  };

  return (
    <div className='ss-coupons'>
      <SectionHeader Icon={meta.Icon} title={meta.title} subtitle={meta.subtitle} />

      <div className='ss-coupons__toolbar'>
        <div className='ss-coupons__categories' role='tablist' aria-label='Coupon categories'>
          {COUPON_CATEGORIES.map((cat) => {
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                type='button'
                role='tab'
                aria-selected={isActive}
                className={
                  isActive
                    ? 'ss-coupons__category ss-coupons__category--active'
                    : 'ss-coupons__category'
                }
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
              </button>
            );
          })}
        </div>

        <button type='button' className='ss-coupons__create-btn' onClick={() => setCreateOpen(true)}>
          <AddIcon sx={{ fontSize: 16 }} />
          Create Coupons
        </button>
      </div>

      <div className='ss-coupons__main'>
        <aside className='ss-coupons__display' aria-label='Featured display coupon'>
          <div className='ss-coupons__display-label'>
            Featured
            <AppTooltip
              title='Select a coupon from the list to feature it for customers on your store.'
              className='app-tooltip__anchor--after-text'
              ariaLabel='About featured display coupon'
            />
          </div>

          <div className='ss-coupons__display-preview'>
            {displayedCoupon ? (
              <div className='ss-coupons__display-preview-card'>
                <span
                  className={
                    displayedCoupon.source === 'platform'
                      ? 'ss-coupon-card__source ss-coupon-card__source--platform'
                      : 'ss-coupon-card__source ss-coupon-card__source--restaurant'
                  }
                >
                  {displayedCoupon.source === 'platform' ? 'Platform' : 'Restaurant'}
                </span>
                <div className='ss-coupon-card__title'>{displayedCoupon.title}</div>
                <div className='ss-coupon-card__subtitle'>{displayedCoupon.subtitle}</div>
                <div className='ss-coupon-card__divider' aria-hidden='true' />
                <div className='ss-coupon-card__footer'>{displayedCoupon.footer}</div>
              </div>
            ) : (
              <div className='ss-coupons__display-empty'>
                <ConfirmationNumberOutlinedIcon sx={{ fontSize: 28, color: '#c4c9d4' }} />
                <span>Tap a coupon to display</span>
              </div>
            )}
          </div>

          <div className='ss-coupons__display-actions'>
            <button
              type='button'
              className='ss-coupons__action-btn ss-coupons__action-btn--primary'
              disabled={!displayedCoupon}
            >
              Display
              <InfoOutlinedIcon sx={{ fontSize: 13 }} />
            </button>
            <button type='button' className='ss-coupons__action-btn' disabled={!displayedCoupon}>
              Edit
            </button>
          </div>
        </aside>

        <div className='ss-coupons__list-wrap'>
          <div className='ss-coupons__list-meta'>
            {filteredCoupons.length} {filteredCoupons.length === 1 ? 'coupon' : 'coupons'}
          </div>
          {filteredCoupons.length === 0 ? (
            <div className='ss-coupons__list-empty'>No coupons in this category yet.</div>
          ) : (
            <div className='ss-coupons__list'>
              {filteredCoupons.map((coupon) => (
                <CouponCard
                  key={coupon.id}
                  coupon={coupon}
                  selected={displayedCouponId === coupon.id}
                  onSelect={(c) => setDisplayedCouponId(c.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <CreateCouponModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
      />
    </div>
  );
}
